import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import { FiUpload, FiImage, FiSearch, FiCheck } from "react-icons/fi";
import { appActions } from "../../../redux/actions/in-app";
import { ImageType } from "../../../types/app/mediaTypes";
import "./MediaSelectorModal.scss";

interface MediaSelectorModalProps {
  show: boolean;
  onHide: () => void;
  onSelect: (imageUrl: string) => void;
  title?: string;
  currentImage?: string;
  // Redux props
  getImageGalleryAction: (payload: any, isNewBatch: boolean) => Promise<any>;
  uploadProductImageAction: (
    payload: any,
    otherNames: any,
    uploadId?: any
  ) => Promise<any>;
  getImageGalleryRequest: {
    is_request_processing: boolean;
    results: ImageType[];
    request_data: {
      response: {
        results: ImageType[];
        totalNumberOfPages: number;
        pageNumber: number;
      };
    };
  };
  uploadProductImageRequest: {
    is_request_processing: boolean;
    results: {
      fileURL: string;
      thumbnail: string;
    };
  };
}

const MediaSelectorModalComponent: React.FC<MediaSelectorModalProps> = ({
  show,
  onHide,
  onSelect,
  title = "Select Image",
  currentImage,
  getImageGalleryAction,
  uploadProductImageAction,
  getImageGalleryRequest,
  uploadProductImageRequest,
}) => {
  const [activeTab, setActiveTab] = useState<string>("library");
  const [selectedImage, setSelectedImage] = useState<string | null>(
    currentImage || null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(12);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);


  const images = getImageGalleryRequest?.request_data?.response?.results || [];
  const totalPages =
    getImageGalleryRequest?.request_data?.response?.totalNumberOfPages || 1;
  const isLoading = getImageGalleryRequest?.is_request_processing || false;
  const isUploading = uploadProductImageRequest?.is_request_processing || false;

  const loadImages = useCallback(async () => {
    const payload = `page=${pageNumber}&size=${pageSize}&fileName=${searchTerm}`;
    await getImageGalleryAction(payload, pageNumber === 1);
  }, [pageNumber, pageSize, searchTerm, getImageGalleryAction]);

  useEffect(() => {
    if (show && activeTab === "library") {
      loadImages();
    }
  }, [show, pageNumber, searchTerm, activeTab, loadImages]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPageNumber(1);
  };

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const handleConfirm = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedImage(currentImage || null);
    setSearchTerm("");
    setPageNumber(1);
    setUploadFile(null);
    setUploadPreview(null);
    onHide();
  };

  // File upload handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPG, PNG, GIF, or WebP)");
      return;
    }

    if (file.size > maxSize) {
      alert("File size must be less than 5MB");
      return;
    }

    setUploadFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const result = await uploadProductImageAction(formData, [], undefined);
      if (result && result.fileURL) {
        setSelectedImage(result.fileURL);
        setActiveTab("library");
        loadImages();
        setUploadFile(null);
        setUploadPreview(null);
      }
    } catch (error) {
      alert("Failed to upload image. Please try again.");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="media-selector-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="tab-navigation">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link
                active={activeTab === "library"}
                onClick={() => setActiveTab("library")}
              >
                <FiImage className="me-2" /> Media Library
              </Nav.Link>
            </Nav.Item>
            {/* Upload tab hidden - users can only select from existing images */}
            {/* <Nav.Item>
              <Nav.Link
                active={activeTab === "upload"}
                onClick={() => setActiveTab("upload")}
              >
                <FiUpload className="me-2" /> Upload New
              </Nav.Link>
            </Nav.Item> */}
          </Nav>
        </div>

        {activeTab === "library" && (
          <div className="library-tab">
            <div className="search-bar mb-3">
              <Form.Control
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              <FiSearch className="search-icon" />
            </div>

            {isLoading ? (
              <div className="loading-state">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p>Loading images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="empty-state">
                <FiImage size={48} />
                <p>No images found</p>
                <Button
                  variant="primary"
                  onClick={() => setActiveTab("upload")}
                >
                  Upload your first image
                </Button>
              </div>
            ) : (
              <>
                <div className="image-grid">
                  {images.map((image) => (
                    <div
                      key={image.id}
                      className={`image-grid-item ${
                        selectedImage === image.imageUrl ? "selected" : ""
                      }`}
                      onClick={() => handleImageSelect(image.imageUrl)}
                    >
                      <img src={image.imageUrl} alt={image.name || "Image"} />
                      {selectedImage === image.imageUrl && (
                        <div className="selected-overlay">
                          <FiCheck size={24} />
                        </div>
                      )}
                      <div className="image-info">
                        <span className="image-name">
                          {image.name || "Untitled"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={pageNumber === 1}
                      onClick={() => setPageNumber(pageNumber - 1)}
                    >
                      Previous
                    </Button>
                    <span className="page-info">
                      Page {pageNumber} of {totalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={pageNumber === totalPages}
                      onClick={() => setPageNumber(pageNumber + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "upload" && (
          <div className="upload-tab">
            {!uploadPreview ? (
              <div
                className={`upload-dropzone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input")?.click()}
              >
                <FiUpload size={48} />
                <p>Drag and drop an image here, or click to browse</p>
                <small>Supported formats: JPG, PNG, GIF, WebP (Max 5MB)</small>
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
              </div>
            ) : (
              <div className="upload-preview">
                <img src={uploadPreview} alt="Upload preview" />
                <div className="upload-actions">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setUploadFile(null);
                      setUploadPreview(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? "Uploading..." : "Upload Image"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedImage}
        >
          Select Image
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  getImageGalleryRequest: state.allInAppReducers?.getImageGalleryReducer || {
    is_request_processing: false,
    results: [],
    request_data: {
      response: {
        results: [],
        totalNumberOfPages: 1,
        pageNumber: 1,
      },
    },
  },
  uploadProductImageRequest: state.allInAppReducers
    ?.uploadProductImageReducer || {
    is_request_processing: false,
    results: {
      fileURL: "",
      thumbnail: "",
    },
  },
});

const mapDispatchToProps = {
  getImageGalleryAction: appActions.GetImageGallery,
  uploadProductImageAction: appActions.UploadProductImage,
};

export const MediaSelectorModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(MediaSelectorModalComponent);
